import type { QuantifiedPlan } from '../../../services/areasService';

const ymdFromPlanField = (raw: string | Date | undefined | null): string => {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
    return raw.trim();
  }
  const d = typeof raw === 'string' ? new Date(raw) : raw;
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Prefers Open plan row for the contract year, then any plan for that year. */
export const planForContractYear = (
  plans: QuantifiedPlan[],
  year: number
): QuantifiedPlan | undefined =>
  plans.find((p) => p.year === year && p.status === 'Open') ??
  plans.find((p) => p.year === year);

export const isYmdInPlanRange = (ymd: string, plan: QuantifiedPlan): boolean => {
  if (!ymd) return true;
  const start = ymdFromPlanField(plan.startDate);
  const end = ymdFromPlanField(plan.endDate);
  if (!start || !end) return true;
  return ymd >= start && ymd <= end;
};

export type ContractYearDateMismatch = {
  contractYear: number;
  planStart: string;
  planEnd: string;
  isOpenPlan: boolean;
  outsideLabels: string[];
};

export function getContractYearDateMismatch(input: {
  isStanding: boolean;
  contractYear: string | null | undefined;
  identifiedDate: string | null | undefined;
  plannedCompletionDate: string | null | undefined;
  quantifiedPlans: QuantifiedPlan[];
}): ContractYearDateMismatch | null {
  if (input.isStanding) return null;

  const cyRaw = input.contractYear?.trim();
  if (!cyRaw) return null;

  const year = Number(cyRaw);
  if (!Number.isFinite(year) || year <= 0) return null;

  const startYmd = input.identifiedDate?.trim() || '';
  const endYmd = input.plannedCompletionDate?.trim() || '';
  if (!startYmd && !endYmd) return null;

  const plan = planForContractYear(input.quantifiedPlans, year);
  if (!plan) return null;

  const planStart = ymdFromPlanField(plan.startDate);
  const planEnd = ymdFromPlanField(plan.endDate);
  if (!planStart || !planEnd) return null;

  const outsideLabels: string[] = [];
  if (startYmd && !isYmdInPlanRange(startYmd, plan)) {
    outsideLabels.push(`planned start (${startYmd})`);
  }
  if (endYmd && !isYmdInPlanRange(endYmd, plan)) {
    outsideLabels.push(`planned completion (${endYmd})`);
  }

  if (outsideLabels.length === 0) return null;

  return {
    contractYear: year,
    planStart,
    planEnd,
    isOpenPlan: plan.status === 'Open',
    outsideLabels,
  };
}

export function formatContractYearDateMismatchMessage(mismatch: ContractYearDateMismatch): string {
  return `Contract year ${mismatch.contractYear} runs ${mismatch.planStart}–${mismatch.planEnd}. The following fall outside that range: ${mismatch.outsideLabels.join(', ')}.`;
}
